import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { later, next, scheduleOnce } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service, Slack } from 'webapp-types';
import type { GoalDeliveryType } from 'webapp/enums/enums';
import { DeliveryType, PopupEvent, ScheduleType, SlackMessage } from 'webapp/enums/enums';

import didInsert from 'webapp/modifiers/did-insert';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormCheckboxSwitchComponent from 'webapp/components/form/checkbox-switch';
import GoalNotificationsPopupComponent from 'webapp/components/goal/notifications/popup';
import ToolTipComponent from 'webapp/components/tool-tip';

import type { GoalLog } from 'webapp/models-custom';
import Goal from 'webapp/models-custom/goal';

export type Model = Goal | GoalLog;

interface Args {
	model: Model;
	submitInProgress: boolean;
	toggleDelivery: () => void;
	isPopupOpen?: boolean;
	onApply?: (
		delivery: GoalDeliveryType[],
		recipients: string[],
		slackChannels: Slack.Channel[],
		scheduleTypes: ScheduleType[],
	) => void;
}

export default class GoalNotificationsSettingsComponent extends Component<Args> {
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('model-manager/goal-logs') declare mmGoalLogsService: Service.ModelManager.GoalLogs;
	@service('popup') declare popupService: Service.Popup;

	el: HTMLElement | null = null;

	get delivery() {
		return this.args.model.delivery;
	}

	get showScheduleTypes() {
		return this.isModelGoal(this.args.model);
	}

	get recipientsShown() {
		return this.args.model.delivery.includes(DeliveryType.EMAIL)
			? this.args.model.recipients
			: [];
	}

	get slackChannelsShown() {
		return this.args.model.delivery.includes(DeliveryType.SLACK)
			? this.args.model.slackChannels
			: [];
	}

	get text() {
		const recipients = this.recipientsShown,
			othersLength = recipients.length,
			slackChannelsLength = this.slackChannelsShown.length;

		let text = 'owner';
		if (othersLength || slackChannelsLength) {
			if (othersLength) {
				text += ` & ${othersLength} other${recipients.length > 1 ? 's' : ''}`;
			}
			if (slackChannelsLength) {
				text += ' & '; // "Slack" word and its tooltip will be added with this.slackInfo
			}
		}

		return text;
	}

	get slackInfo() {
		const slackChannels = this.slackChannelsShown;
		if (!slackChannels.length) {
			return null;
		}

		return {
			text: 'Slack',
			tooltip: slackChannels
				.map((channel) => {
					if (channel.id === SlackMessage.DIRECT) {
						return 'DM';
					} else {
						return `#${channel.name}`;
					}
				})
				.join('<br>'),
		};
	}

	@action
	openPopup() {
		if (this.args.submitInProgress) {
			return;
		}

		if (this.popupService.isOpen(GoalNotificationsPopupComponent)) {
			return;
		}

		const attachTo = this.el?.querySelector<HTMLElement>('.settings-icon');

		if (!attachTo) {
			return;
		}

		const popup = this.popupService.createContext(GoalNotificationsPopupComponent, attachTo, {
			allowStacked: true,
			popupClass: 'popup-type-goal-notifications',
		});

		const originalModel = this.args.model,
			modelCopy = this.isModelGoal(originalModel)
				? this.mmGoalsService.createModelCopy(originalModel)
				: this.mmGoalLogsService.createModelCopy(originalModel);

		popup.open({
			model: modelCopy,
			onSave: () => {
				if (this.args.onApply) {
					const { delivery, recipients, slackChannels, scheduleTypes } =
						modelCopy as Goal;
					this.args.onApply(delivery, recipients, slackChannels, scheduleTypes);
				} else {
					originalModel.delivery = modelCopy.delivery;
					originalModel.recipients = modelCopy.recipients;
					originalModel.slackChannels = modelCopy.slackChannels;
					if (this.isModelGoal(originalModel)) {
						originalModel.scheduleTypes = (modelCopy as Goal).scheduleTypes;
					}
				}
			},
		});

		popup.on(PopupEvent.CLOSED, this, () => {
			next(() => modelCopy.delete(true, true));
		});
	}

	@action
	setupView(node: HTMLElement): void {
		this.el = node;

		if (this.args.isPopupOpen) {
			later(
				this,
				() => {
					scheduleOnce('afterRender', this, this.openPopup);
				},
				500,
			);
		}
	}

	private isModelGoal(model: Model): model is Goal {
		return model instanceof Goal;
	}
	<template>
		<div class="goal-notifications" {{didInsert this.setupView}}>
			<div class="goal-notifications--switch">
				<FormCheckboxSwitchComponent
					@name="notifications"
					@selected={{@model.isDeliveryOn}}
					@onChange={{@toggleDelivery}}
					@isDisabled={{@submitInProgress}}
					@size="small"
				/>
				<ToolTipComponent @text="Turn all notifications on or off" @position="top" />
			</div>
			<label>
				{{#if this.showScheduleTypes}}
					Notify
				{{else}}
					Send update to
				{{/if}}
				<em>
					{{this.text}}
					{{#if this.slackInfo}}
						<span>
							{{this.slackInfo.text}}
							<ToolTipComponent @text={{this.slackInfo.tooltip}} />
						</span>
					{{/if}}
				</em>
				{{#if this.showScheduleTypes}}
					about progress
				{{/if}}
			</label>
			<a class="settings-icon" {{on "click" this.openPopup}}>
				<CommonUiIconComponent @name="Settings" @size="sm" />
			</a>
		</div>
	</template>
}
